import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useBusinessSearch } from './BusinessSearchContext';

const BusinessContext = createContext();

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

export const BusinessProvider = ({ children }) => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { user } = useAuth();
  const location = useLocation();
  const businessSearch = useBusinessSearch();
  
  // Use selected business from BusinessSearchContext
  const selectedBusiness = businessSearch?.selectedBusiness || null;

  // 🚀 PERFORMANCE: Use new lazy loading approach - load detailed data only when needed
  useEffect(() => {
    if (user && businessSearch) {
      console.log('🚀 PERFORMANCE: User authenticated, businesses will be loaded by BusinessSearchContext');
      // BusinessSearchContext handles minimal business loading
      // We only load full business data when specifically requested
    } else {
      console.log('🔒 SECURITY: No user, clearing all business data');
      setBusinesses([]);
    }
  }, [user, businessSearch]);

  // 🚀 PERFORMANCE: Load detailed business data on-demand (lazy loading)
  const loadBusinesses = async (forceReload = false) => {
    console.log('🚀 PERFORMANCE: loadBusinesses called - using new lazy loading approach');
    
    // Use the new BusinessSearchContext for loading
    if (businessSearch) {
      if (forceReload) {
        await businessSearch.refreshBusinesses();
      }
      // Convert minimal businesses to full business format for backward compatibility
      const minimalBusinesses = businessSearch.minimalBusinesses;
      const compatibleBusinesses = minimalBusinesses.map(business => ({
        ...business,
        // Add empty fields for backward compatibility
        description: '',
        categories: [],
        phoneNumber: '',
        websiteUrl: '',
        lat: null,
        lng: null,
        coordinates: { lat: null, lng: null },
        localRankings: [],
        keywords: [],
        rankingSnapshots: [],
        optimizationScore: 0,
        lastAnalyzedAt: null,
        recommendations: [],
        metrics: {
          viewsLastMonth: 0,
          callsLastMonth: 0,
          directionsLastMonth: 0,
          websiteClicksLastMonth: 0
        }
      }));
      
      setBusinesses(compatibleBusinesses);
      return compatibleBusinesses;
    }
    
    return [];
  };

  // Connect to Google My Business (full business scopes, separate from SSO login)
  const connectToGoogle = () => {
    window.location.href = `${window.location.protocol}//${window.location.hostname}:5001/api/auth/google-business`;
  };

  // Get business recommendations
  const getBusinessRecommendations = async (businessId) => {
    try {
      const response = await axios.get(`/api/businesses/${businessId}/recommendations`);
      return response.data.recommendations;
    } catch (error) {
      console.error('Error getting recommendations:', error);
      throw error;
    }
  };

  // Generate AI recommendations for a business
  const generateRecommendations = async (businessId) => {
    try {
      const response = await axios.post(`/api/businesses/${businessId}/analyze`);
      
      // Update the business in our state with new recommendations
      setBusinesses(prev => prev.map(business => 
        business.id === businessId 
          ? { ...business, recommendations: response.data.recommendations, lastAnalyzedAt: new Date() }
          : business
      ));
      
      return response.data.recommendations;
    } catch (error) {
      console.error('Error generating recommendations:', error);
      throw error;
    }
  };

  // Apply a recommendation
  const applyRecommendation = async (businessId, recommendationId, updatedData) => {
    try {
      const response = await axios.post(
        `/api/businesses/${businessId}/recommendations/${recommendationId}/apply`,
        updatedData
      );
      
      // Update business in state
      setBusinesses(prev => prev.map(business => 
        business.id === businessId 
          ? { ...business, ...response.data.business }
          : business
      ));
      
      return response.data;
    } catch (error) {
      console.error('Error applying recommendation:', error);
      throw error;
    }
  };

  // Generate a business post
  const generatePost = async (businessId, postType = 'general', occasion = null) => {
    try {
      const response = await axios.post(`/api/businesses/${businessId}/posts/generate`, {
        postType,
        occasion
      });
      return response.data.post;
    } catch (error) {
      console.error('Error generating post:', error);
      throw error;
    }
  };

  // Update business data
  const updateBusiness = async (businessId, updates) => {
    try {
      const response = await axios.put(`/api/businesses/${businessId}`, updates);
      
      // Update business in state
      setBusinesses(prev => prev.map(business => 
        business.id === businessId 
          ? { ...business, ...response.data.business }
          : business
      ));
      
      return response.data.business;
    } catch (error) {
      console.error('Error updating business:', error);
      throw error;
    }
  };

  // Navigate to a specific business and feature
  const navigateToBusinessFeature = (businessId, feature = 'dashboard') => {
    const business = businesses.find(b => 
      b.id === businessId || 
      b.locationId === businessId ||
      b.googleLocationId === businessId
    );
    
    if (business) {
      setSelectedBusiness(business);
      
      // Generate the correct URL path - prioritize locationId field
      const locationId = business.locationId || business.googleLocationId || business.id;
      
      if (!locationId) {
        console.error('No valid locationId found for business:', business);
        return null;
      }
      
      if (feature === 'dashboard') {
        return '/agency-dashboard';
      } else {
        return `/${feature}/${locationId}`;
      }
    }
    
    return null;
  };

  // Get business by any identifier - removed duplicate, using enhanced version below

  // Smart business selection based on URL
  const selectBusinessFromUrl = (pathname) => {
    const routeLocationId = pathname.match(/\/[^/]+\/(\d+)/)?.[1];
    
    if (routeLocationId && businesses.length > 0) {
      const matchingBusiness = businesses.find(b => 
        b.locationId === routeLocationId || 
        b.googleLocationId === routeLocationId ||
        b.id === routeLocationId
      );
      
      if (matchingBusiness && (!selectedBusiness || selectedBusiness.id !== matchingBusiness.id)) {
        console.log(`🔄 Auto-selecting business from URL: ${matchingBusiness.businessName}`);
        setSelectedBusiness(matchingBusiness);
        return matchingBusiness;
      }
    }
    
    return selectedBusiness;
  };

  // Enhanced business selection with lazy loading
  const setSelectedBusiness = async (business, loadDetails = false) => {
    if (businessSearch) {
      return await businessSearch.selectBusiness(business, loadDetails);
    }
    return business;
  };

  // Get business details with lazy loading
  const getBusinessDetails = async (businessId, forceRefresh = false) => {
    if (businessSearch) {
      return await businessSearch.loadBusinessDetails(businessId, forceRefresh);
    }
    return null;
  };

  // Enhanced getBusinessById
  const getBusinessById = (businessId) => {
    if (businessSearch) {
      return businessSearch.getBusinessById(businessId);
    }
    return businesses.find(b => 
      b.id === businessId || 
      b.locationId === businessId ||
      b.googleLocationId === businessId
    ) || null;
  };

  const value = {
    businesses,
    selectedBusiness,
    setSelectedBusiness,
    loading: loading || businessSearch?.loading || false,
    error: error || businessSearch?.error || null,
    loadBusinesses,
    connectToGoogle,
    getBusinessRecommendations,
    generateRecommendations,
    applyRecommendation,
    generatePost,
    updateBusiness,
    navigateToBusinessFeature,
    getBusinessById,
    selectBusinessFromUrl,
    // New methods for enhanced functionality
    getBusinessDetails,
    minimalBusinesses: businessSearch?.minimalBusinesses || [],
    searchBusinesses: businessSearch?.searchBusinesses || (() => []),
    refreshBusinesses: businessSearch?.refreshBusinesses || (() => {})
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};